/**
 * Display-formatting helpers. Local time formatting is intentionally
 * timezone-dependent (uses the browser's local timezone, per DESIGN.md
 * "Timestamps: local time displayed"); the UTC tooltip formatter is
 * timezone-independent and safe to unit test for an exact value.
 */

export function formatLocalTime(ms: number): string {
  return new Date(ms).toLocaleTimeString("pl-PL", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
}

export function formatUtcTooltip(ms: number): string {
  const iso = new Date(ms).toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

/** Timezone mode for the primary time display (DESIGN.md Aneks M002 §A4). */
export type TimezoneMode = "local" | "utc";

/** "HH:MM" in UTC, no suffix — used internally and by formatTimeForMode. */
export function formatUtcTime(ms: number): string {
  const d = new Date(ms);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Primary clock label for the active timezone mode. UTC mode carries the
 * "UTC" suffix so the reader never mistakes it for local time (§A4).
 */
export function formatTimeForMode(ms: number, mode: TimezoneMode): string {
  return mode === "utc" ? `${formatUtcTime(ms)} UTC` : formatLocalTime(ms);
}

/**
 * Tooltip showing the zone OPPOSITE the active primary mode (§A4: "tooltip
 * pokazuje strefę przeciwną"): local mode -> UTC tooltip (existing
 * formatUtcTooltip); UTC mode -> local date+time tooltip.
 */
export function formatOppositeZoneTooltip(ms: number, primaryMode: TimezoneMode): string {
  if (primaryMode === "utc") {
    const d = new Date(ms);
    const date = d.toLocaleDateString("sv-SE"); // YYYY-MM-DD, locale-stable
    return `${date} ${formatLocalTime(ms)} lok.`;
  }
  return formatUtcTooltip(ms);
}

/**
 * Minutes-to-add-to-UTC offset for the browser's local timezone, in the
 * direction computeMajorTicks (timeline.ts) expects. `0` for UTC mode.
 */
export function timezoneOffsetMinutesForMode(mode: TimezoneMode): number {
  return mode === "utc" ? 0 : -new Date().getTimezoneOffset();
}
